import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchConfig, updateConfig, resetConfig, type BettingConfigUpdate } from '../api/client';
import { NumberInput, SliderInput } from '../components/forms';

export default function Settings() {
  const queryClient = useQueryClient();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [errors, setErrors] = useState<string[]>([]);

  const { data: config, isLoading } = useQuery({
    queryKey: ['config'],
    queryFn: fetchConfig,
  });

  const updateMutation = useMutation({
    mutationFn: updateConfig,
    onMutate: () => {
      setSaveStatus('saving');
      setErrors([]);
    },
    onSuccess: (data) => {
      if (data.success) {
        setSaveStatus('saved');
        queryClient.invalidateQueries({ queryKey: ['config'] });
        setTimeout(() => setSaveStatus('idle'), 2000);
      } else {
        setSaveStatus('error');
        setErrors(data.errors || ['Unknown error']);
      }
    },
    onError: () => {
      setSaveStatus('error');
      setErrors(['Failed to update configuration']);
    },
  });

  const resetMutation = useMutation({
    mutationFn: resetConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config'] });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
  });

  const handleUpdate = (updates: BettingConfigUpdate) => {
    updateMutation.mutate(updates);
  };

  const handleBettingToggle = () => {
    if (config?.betting.enabled) {
      // Turning off - no confirmation needed
      handleUpdate({ bettingEnabled: false });
    } else {
      // Turning on - show confirmation
      setShowConfirmDialog(true);
    }
  };

  const confirmBettingEnable = () => {
    handleUpdate({ bettingEnabled: true });
    setShowConfirmDialog(false);
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Settings</h1>
        <div className="flex items-center gap-3">
          {saveStatus === 'saving' && (
            <span className="text-sm text-gray-400">Saving...</span>
          )}
          {saveStatus === 'saved' && (
            <span className="text-sm text-success">Saved!</span>
          )}
          {saveStatus === 'error' && (
            <span className="text-sm text-danger">Error</span>
          )}
          <button
            onClick={() => resetMutation.mutate()}
            className="px-3 py-1.5 text-sm border border-gray-600 rounded hover:bg-surface-50 transition-colors"
          >
            Reset to Defaults
          </button>
        </div>
      </div>

      {/* Error Display */}
      {errors.length > 0 && (
        <div className="bg-danger/10 border border-danger/30 rounded-lg p-4">
          <h3 className="font-medium text-danger mb-2">Validation Errors</h3>
          <ul className="list-disc list-inside text-sm text-danger/80">
            {errors.map((error, i) => (
              <li key={i}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* System Info */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">System Information</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-400">Version</div>
            <div className="font-mono">{config?.version}</div>
          </div>
          <div>
            <div className="text-sm text-gray-400">Cron Schedule</div>
            <div className="font-mono">{config?.polling.cronSchedule}</div>
          </div>
        </div>
      </div>

      {/* Betting Mode Toggle */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Betting Mode</h2>
        <div className="flex items-center justify-between p-4 bg-surface-50 rounded-lg">
          <div>
            <div className="flex items-center gap-3">
              <span
                className={`w-3 h-3 rounded-full ${
                  config?.betting.enabled ? 'bg-success animate-pulse' : 'bg-warning'
                }`}
              />
              <span className="font-medium">
                {config?.betting.enabled ? 'Live Trading' : 'Paper Trading'}
              </span>
            </div>
            <p className="text-sm text-gray-400 mt-1">
              {config?.betting.enabled
                ? 'Real bets are being placed on Polymarket'
                : 'Simulating bets without real execution'}
            </p>
          </div>
          <button
            onClick={handleBettingToggle}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              config?.betting.enabled ? 'bg-success' : 'bg-gray-600'
            }`}
          >
            <span
              className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                config?.betting.enabled ? 'translate-x-8' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Risk Parameters */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Risk Parameters</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <NumberInput
            label="Max Bet Size"
            value={config?.betting.maxBetSize || 100}
            min={1}
            max={10000}
            step={10}
            prefix="$"
            description="Maximum single bet amount in USD"
            onChange={(value) => handleUpdate({ maxBetSize: value })}
          />
          <NumberInput
            label="Max Daily Loss"
            value={config?.betting.maxDailyLoss || 500}
            min={10}
            max={50000}
            step={50}
            prefix="$"
            description="Stop trading after this loss amount"
            onChange={(value) => handleUpdate({ maxDailyLoss: value })}
          />
          <NumberInput
            label="Max Concurrent Bets"
            value={config?.betting.maxConcurrentBets || 5}
            min={1}
            max={50}
            step={1}
            description="Maximum open positions at once"
            onChange={(value) => handleUpdate({ maxConcurrentBets: value })}
          />
          <NumberInput
            label="Cooldown Period"
            value={config?.betting.cooldownSeconds || 300}
            min={0}
            max={86400}
            step={60}
            suffix="s"
            description="Wait time after a losing bet"
            onChange={(value) => handleUpdate({ cooldownSeconds: value })}
          />
        </div>
      </div>

      {/* Edge & Kelly Settings */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Edge & Sizing</h2>
        <div className="space-y-6">
          <SliderInput
            label="Minimum Edge Threshold"
            value={(config?.betting.minEdgeThreshold || 0.03) * 100}
            min={1}
            max={20}
            step={0.5}
            formatValue={(v) => `${v.toFixed(1)}%`}
            description="Only bet when edge exceeds this threshold"
            onChange={(value) => handleUpdate({ minEdgeThreshold: value / 100 })}
            showMarks
          />
          <SliderInput
            label="Kelly Fraction"
            value={(config?.betting.kellyFraction || 0.25) * 100}
            min={5}
            max={100}
            step={5}
            formatValue={(v) => `${v.toFixed(0)}%`}
            description="Fraction of Kelly Criterion for position sizing (25% = quarter Kelly)"
            onChange={(value) => handleUpdate({ kellyFraction: value / 100 })}
            showMarks
          />
        </div>
      </div>

      {/* API Status */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">API Configuration</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-surface-50 rounded">
            <div className="flex items-center gap-3">
              <span className="text-lg">📊</span>
              <span>The Odds API</span>
            </div>
            <StatusIndicator configured={config?.polling.oddsApiConfigured || false} />
          </div>
          <div className="flex items-center justify-between p-3 bg-surface-50 rounded">
            <div className="flex items-center gap-3">
              <span className="text-lg">🔮</span>
              <span>Polymarket CLOB</span>
            </div>
            <StatusIndicator configured={config?.polling.polymarketConfigured || false} />
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-4">
          API keys are managed via Cloudflare dashboard secrets.
        </p>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Enable Live Trading?</h3>
            <p className="text-gray-400 mb-6">
              This will enable real money betting on Polymarket. Make sure your wallet
              has sufficient USDC balance and all API keys are configured correctly.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 border border-gray-600 rounded hover:bg-surface-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmBettingEnable}
                className="px-4 py-2 bg-success text-white rounded hover:bg-success/80"
              >
                Enable Live Trading
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusIndicator({ configured }: { configured: boolean }) {
  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-medium ${
        configured
          ? 'bg-success/20 text-success'
          : 'bg-danger/20 text-danger'
      }`}
    >
      {configured ? 'Configured' : 'Not Configured'}
    </span>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 w-32 bg-surface-50 rounded"></div>
      <div className="h-32 bg-surface-50 rounded-lg"></div>
      <div className="h-48 bg-surface-50 rounded-lg"></div>
      <div className="h-24 bg-surface-50 rounded-lg"></div>
    </div>
  );
}
